import { useState } from 'react'

import useValidation from '../../../hooks/use-validation'
import PaymentCard from '../../../components/payment-card'
import Calendar from '../../../components/svg/calendar'
import { Select, FormField, PriceFormField, NumberFormField } from '../../../components/inputs'

const customStyles = {
  control: (base, state) => ({
    ...base,
    border: state.selectProps.error
      ? '0.2rem solid var(--alert-color) !important'
      : '0.2rem solid var(--secondary-input-bg) !important',
  }),
}

function generateAmountString(amount) {
  amount = +amount

  if (Number.isNaN(amount) || amount <= 0) return 'N/A'
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

export default function Calculator({
  price,
  disabled,
  rates: { monthlyPayment, totalInterest, totalPayment },
  properties: { paymentFrequencies, terms },
  onPress,
}) {
  const [form, setForm] = useState({
    term: '',
    interestRate: '',
    paymentFrequency: '',
    purchase: price,
    downPayment: '',
  })

  const [showErrors, { isFormValid, errors }] = useValidation(form, validate)

  const handleClick = () => {
    if (!isFormValid) showErrors()
    else onPress({ ...form, downPayment: form.downPayment === '' ? 0 : form.downPayment })
  }

  return (
    <>
      <div className='paymentCardWrapper grid'>
        <div className='paymentCard'>
          <PaymentCard
            icon={<Calendar />}
            amount={
              monthlyPayment <= 0 || totalInterest <= 0 || totalPayment <= 0
                ? 'N/A'
                : generateAmountString(monthlyPayment)
            }
            title='Payment'
            description='Based on selected frequency'
          />
        </div>
        <div className='paymentCard'>
          <PaymentCard
            icon={<Calendar />}
            amount={
              monthlyPayment <= 0 || totalInterest <= 0 || totalPayment <= 0
                ? 'N/A'
                : generateAmountString(totalInterest)
            }
            title='Interest'
            description='Total interest paid over term '
          />
        </div>
        <div className='paymentCard'>
          <PaymentCard
            icon={<Calendar />}
            amount={
              monthlyPayment <= 0 || totalInterest <= 0 || totalPayment <= 0
                ? 'N/A'
                : generateAmountString(totalPayment)
            }
            title='Total Paid'
            description='Price including tax and interest'
          />
        </div>
      </div>

      <div className='grid secondRow'>
        <div className='term'>
          <span>Term</span>
          <div>
            <Select
              isDisabled={disabled}
              styles={customStyles}
              options={(terms || []).map((term) => ({ label: term, value: term }))}
              onChange={({ value }) => setForm({ ...form, term: value })}
              error={errors.term}
              isSearchable={false}
              maxMenuHeight={200}
              placeholder='Select'
            />
          </div>
        </div>

        <div className='term'>
          <span>Interest rate</span>
          <div>
            <NumberFormField
              id='interestRate'
              name='interestRate'
              inputMode='numeric'
              disabled={disabled}
              error={errors.interestRate}
              value={form.interestRate || ''}
              onChange={(e) => setForm({ ...form, interestRate: e.target.value })}
              data-hj-allow
            />
          </div>
        </div>

        <div className='term'>
          <span>Frequency</span>
          <div>
            <Select
              isDisabled={disabled}
              styles={customStyles}
              options={Object.entries(paymentFrequencies || {}).map(([key, val]) => ({
                label: val,
                value: key,
              }))}
              onChange={({ value }) => setForm({ ...form, paymentFrequency: value })}
              error={errors.paymentFrequency}
              isSearchable={false}
              maxMenuHeight={200}
              placeholder='Select'
            />
          </div>
        </div>
      </div>

      <div className='grid thirdRow'>
        <div className='third'>
          <span>Purchase</span>
          <div>
            <FormField disabled={true} value={generateAmountString(price)} data-hj-allow />
          </div>
        </div>

        <div className='third'>
          <span>Down Payment</span>

          <div>
            <PriceFormField
              disabled={disabled}
              value={form.downPayment || ''}
              onChange={(e) => setForm({ ...form, downPayment: e.target.value })}
              error={errors.downPayment}
              name='downPayment'
              id='downPayment'
              data-hj-allow
            />
          </div>
        </div>

        <div className='third flex alignEnd'>
          <button disabled={disabled} className='calculate' onClick={handleClick}>
            Calculate
          </button>
        </div>
      </div>

      <style jsx>{`
        .calculate {
          width: 100%;
          max-width: 43.3rem;
          min-width: 33.3rem;
          height: 4.2rem;
          border-radius: 3.9rem;
          transition: opacity 0.2s;
          color: var(--primary-button-color);
          font-weight: 700;
          font-size: 1.8rem;
          background: var(--primary-button-bg);
        }

        .secondRow {
          padding: 0 0 4.15rem;
          border-bottom: 0.1rem solid var(--secondary-input-bg);
        }

        .thirdRow {
          margin: 4.15rem 0 0;
        }

        .term,
        .third {
          width: 100%;
          max-width: 43.3rem;
          min-width: 33.3rem;

          span {
            line-height: 2.1rem;
            font-weight: 700;
            font-size: 1.6rem;
            color: var(--secondary-text-color);
          }

          div {
            margin: 1.2rem 0 0;
          }
        }

        .paymentCardWrapper {
          margin: 0 0 3.4rem;

          .paymentCard {
            width: 100%;
            max-width: 43.3rem;
            min-width: 33.3rem;
          }
        }

        .grid {
          display: grid;
          grid-gap: 2.9rem;
          grid-template-columns: repeat(auto-fill, minmax(36.3rem, 1fr));
        }

        @media (max-width: 1024px) {
          .grid {
            grid-template-columns: 1fr;
            align-self: center;
            grid-gap: 2rem;
            place-items: center;
          }

          .paymentCardWrapper,
          .secondRow,
          .thirdRow {
            width: 100%;
          }

          .secondRow.grid,
          .thirdRow.grid {
            grid-template-columns: calc(1fr - 2.8rem);
          }

          .secondRow,
          .thirdRow {
            padding: 0 14px;
            box-sizing: border-box;
          }

          .term,
          .third {
            width: 100%;
            min-width: 32.3rem;

            span {
              line-height: 2.1rem;
              font-weight: 700;
              font-size: 1.2rem;
              color: var(--secondary-text-color);
            }

            div {
              margin: 1rem 0 0;
            }
          }

          .paymentCardWrapper {
            &.grid {
              grid-gap: 0.6rem;
            }

            .paymentCard + .paymentCard {
              margin: 0;
            }

            .paymentCard {
              max-width: 100%;
            }
          }

          .secondRow {
            border: none;
          }

          .thirdRow {
            margin: 2rem 0 0;
          }

          .calculate {
            height: 3.5rem;
            line-height: 1.6rem;
            font-size: 1.2rem;
            margin: 0.4rem 0 0;
          }
        }
      `}</style>
    </>
  )
}

function validate(form) {
  const errors = {}
  let isValid = true

  Object.entries(form).forEach(([key, val]) => {
    if (val === '') {
      errors[key] = true
      isValid = false
    }
  })

  return {
    isValid,
    errors,
  }
}
